import { useMemo } from 'react';
import { isTruthy } from '@chainflip/utils/guard';
import { type InjectedAccountWithMeta, type ReadonlyAccount } from '@/shared/hooks/usePolkadot';
import { entries } from '@/shared/utils/objects';
import {
  type TradingStrategy,
  type StrategyTemplate,
  type StrategyTemplateKey,
} from '@/shared/utils/strategies';
import TokenAmount from '@/shared/utils/TokenAmount';

export const useStrategyTemplates = (
  activeStrategies: TradingStrategy[] | undefined,
  account?: InjectedAccountWithMeta | ReadonlyAccount | null,
) =>
  useMemo(() => {
    if (!activeStrategies) return undefined;

    const groupedStrategies =
      activeStrategies.reduce((acc: Record<StrategyTemplateKey, TradingStrategy[]>, strategy) => {
        acc[strategy.templateKey] ??= [];
        acc[strategy.templateKey].push(strategy);
        return acc;
      }, {}) ?? {};

    const allTemplates = Object.entries(groupedStrategies).map(
      ([templateKey, strategies]): StrategyTemplate => {
        const reportingDataItems = strategies.map((s) => s.reportingData).filter(isTruthy);
        const totalFilledVolumeUsd = reportingDataItems.reduce(
          (acc, item) => acc + item.filledVolumeUsd,
          0,
        );

        // exclude strategies that did not fill any volume yet when calculating apy of template
        const apyReportingDataItems = reportingDataItems.filter((d) => d.pnlUsd);
        const apyPercent =
          apyReportingDataItems.length > 0
            ? apyReportingDataItems.reduce((acc, item) => acc + item.apyPercent, 0) /
              apyReportingDataItems.length
            : 0;

        const pnl24HourUsd =
          apyReportingDataItems.length > 0
            ? apyReportingDataItems.reduce((acc, item) => acc + item.pnl24HourUsd, 0)
            : 0;

        return {
          templateKey: templateKey as StrategyTemplateKey,
          baseAsset: strategies[0].baseAsset,
          quoteAsset: strategies[0].quoteAsset,
          type: strategies[0].type,
          totalBaseLiquidity: strategies.reduce(
            (acc, item) => acc.add(item.baseLiquidity),
            TokenAmount.fromAsset(0, strategies[0].baseAsset),
          ),
          totalQuoteLiquidity: strategies.reduce(
            (acc, item) => acc.add(item.quoteLiquidity),
            TokenAmount.fromAsset(0, strategies[0].quoteAsset),
          ),
          totalBaseLiquidityUsd: strategies.reduce((acc, item) => acc + item.baseLiquidityUsd, 0),
          totalQuoteLiquidityUsd: strategies.reduce((acc, item) => acc + item.quoteLiquidityUsd, 0),
          instanceCount: strategies.length,
          ownInstance: strategies.find((item) => item.ownerAccountSs58Id === account?.idSs58),
          reportingData: reportingDataItems.length
            ? { totalFilledVolumeUsd, apyPercent, pnl24HourUsd }
            : undefined,
        };
      },
    );

    const highlightedTemplates = {
      popular: allTemplates.toSorted((a, b) => b.instanceCount - a.instanceCount).at(0),
      apy: allTemplates
        .toSorted((a, b) => (b.reportingData?.apyPercent ?? 0) - (a.reportingData?.apyPercent ?? 0))
        .at(0),
      volume: allTemplates
        .toSorted(
          (a, b) =>
            (b.reportingData?.totalFilledVolumeUsd ?? 0) -
            (a.reportingData?.totalFilledVolumeUsd ?? 0),
        )
        .at(0),
    };

    const topByApy = allTemplates
      .toSorted((a, b) => (b.reportingData?.apyPercent ?? 0) - (a.reportingData?.apyPercent ?? 0))
      .slice(0, 3);

    const pillsByTemplateKey = entries(highlightedTemplates).reduce(
      (acc, [key, template]) => {
        if (!template) return acc;
        acc[template.templateKey] ??= [];
        acc[template.templateKey]?.push(key as 'popular' | 'volume' | 'apy');

        return acc;
      },
      {} as Record<string, ('popular' | 'volume' | 'apy')[] | undefined>,
    );

    return { all: allTemplates, highlighted: highlightedTemplates, pillsByTemplateKey, topByApy };
  }, [activeStrategies, account]);
